import React from 'react';
import { Formik, Form, Field } from 'formik';
import { Box, Flex, FormControl, Stack } from '@chakra-ui/react';
import * as Yup from 'yup';
import { CustomButton } from "../../../../components/Styled/StyledButtons";
import { Icon } from "@iconify/react";
import { ThemedStyledInput } from "../../../../components/Styled";
import FieldErrorMessage from "../../../../components/FieldErrorMessage/FieldErrorMessage";
import useSingleToast from "../../../../hooks/UseSingleToast/UseSingleToast";

const validationSchema = Yup.object({
    email: Yup.string()
        .email('Search & Filters Error: Invalid email address, please input a valid email address')
        .max(50, 'Search & Filters Error: Email must be 50 characters or less')
        .trim('Search & Filters Error: Email cannot have whitespace')
        .strict(true),
    firstName: Yup.string()
        .max(30, 'Search & Filters Error: First Name must be 30 characters or less')
        .trim('Search & Filters Error: First Name cannot have whitespace')
        .strict(true),
    lastName: Yup.string()
        .max(30, 'Search & Filters Error: Last Name must be 30 characters or less')
        .trim('Search & Filters Error: Last Name cannot have whitespace')
        .strict(true),
    phone: Yup.string()
        .matches(/^[0-9]+$/, 'Search & Filters Error: Phone number must be numeric')
        .min(7, 'Search & Filters Error: Phone number must be at least 10 digits')
        .max(15, 'Search & Filters Error: Phone number must not exceed 15 digits')
});

const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    createdDate: '',
    startDate: '',
    endDate: ''
};

const CustomerSearchFilter = ({ onFilterChange }) => {
    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={(values, { setSubmitting }) => {
                const filters = { ...values };
                if (!filters.startDate || !filters.endDate) {
                    delete filters.startDate;
                    delete filters.endDate;
                }
                if (!filters.createdDate) {
                    delete filters.createdDate;
                }
                onFilterChange(filters);
                setSubmitting(false);
            }}
            onReset={() => onFilterChange(initialValues)}
        >
            {({ handleSubmit, resetForm }) => (
                <Form>
                    <Stack spacing={3}>

                        {/* Row 1: Basic Info */}
                        <Flex gap={4} flexWrap="wrap">
                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="firstName" name="firstName" type="text" placeholder="First Name" />
                                <Box fontSize="xs" color="gray.300" mt={1}>Customer First Name</Box>
                            </FormControl>

                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="lastName" name="lastName" type="text" placeholder="Last Name" />
                                <Box fontSize="xs" color="gray.300" mt={1}>Customer Last Name</Box>
                            </FormControl>

                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="email" name="email" type="email" placeholder="Email" />
                                <Box fontSize="xs" color="gray.300" mt={1}>Customer Email</Box>
                            </FormControl>

                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="phone" name="phone" type="text" placeholder="Phone Number" />
                                <Box fontSize="xs" color="gray.300" mt={1}>Customer Phone</Box>
                            </FormControl>
                        </Flex>

                        {/* Row 2: Date Filters + Buttons */}
                        <Flex gap={4} flexWrap="wrap" align="flex-start">
                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="createdDate" name="createdDate" type="date" placeholder="Created Date" />
                                <Box fontSize="xs" color="gray.300" mt={1}>Search by Created Date</Box>
                            </FormControl>

                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="startDate" name="startDate" type="date" placeholder="Start Date" />
                                <Box fontSize="xs" color="gray.300" mt={1}>Start of Date Range</Box>
                            </FormControl>

                            <FormControl flex="1" minW="250px">
                                <Field as={ThemedStyledInput} id="endDate" name="endDate" type="date" placeholder="End Date" />
                                <Box fontSize="xs" color="gray.300" mt={1}>End of Date Range</Box>
                            </FormControl>

                            {/* Buttons */}
                            <Box display="flex" alignItems="flex-end" gap={2} minW="120px">
                                <CustomButton
                                    onClick={handleSubmit}
                                    type="search"
                                    tooltipLabel="Search"
                                    icon={<Icon icon="bi:search" />}
                                    showLabel={false}
                                >
                                    Search
                                </CustomButton>
                                <CustomButton
                                    type="reset"
                                    onClick={() => {
                                        resetForm();
                                        onFilterChange(initialValues);
                                    }}
                                    tooltipLabel="Clear Filters"
                                    icon={<Icon icon="radix-icons:reset" />}
                                    showLabel={false}
                                >
                                    Clear
                                </CustomButton>
                            </Box>
                        </Flex>

                        {/* Error Messages */}
                        <FieldErrorMessage name="firstName" />
                        <FieldErrorMessage name="lastName" />
                        <FieldErrorMessage name="email" />
                        <FieldErrorMessage name="phone" />
                    </Stack>
                </Form>
            )}
        </Formik>
    );
};

export default CustomerSearchFilter;
